import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cross-origin Bearer fallback: when the deployed static frontend lives on
// a different subdomain than the backend (e.g. *.preview.static.emergentagent.com
// vs *.preview.emergentagent.com), browsers block the SameSite=None cookie.
// We persist the session_token in localStorage and inject it as
// Authorization: Bearer on every request. The backend's /auth/me already
// accepts the Bearer header as a fallback when no cookie is present.
//
// We also DISABLE withCredentials globally because:
//   - cross-origin: the Cloudflare proxy returns CORS `Access-Control-Allow-Origin: *`
//     which browsers reject when combined with credentials → request blocked.
//     Bearer works because it's a regular header (allow-headers: *).
//   - same-origin: cookies are sent/received regardless of withCredentials.
axios.defaults.withCredentials = false;
// Helper que setea headers de forma segura tanto en objetos plain como en
// instancias AxiosHeaders de axios v1 (donde el proxy de `.headers` puede
// ignorar assignments directos en algunos entornos).
const _setHeader = (config, name, value) => {
  if (!config.headers) config.headers = {};
  const existing = (typeof config.headers.get === 'function')
    ? config.headers.get(name)
    : config.headers[name];
  if (existing) return;
  if (typeof config.headers.set === 'function') {
    config.headers.set(name, value);
  } else {
    config.headers[name] = value;
  }
};

axios.interceptors.request.use((config) => {
  // Force withCredentials off everywhere — many call sites pass
  // `{ withCredentials: true }` explicitly, which would override the default.
  // Cross-origin deploys can't use credentials (the proxy returns ACAO: *),
  // and same-origin requests don't need it: cookies are sent regardless.
  config.withCredentials = false;
  try {
    // 1) Si hay sesión Google (cliente App) usar ese token.
    const t = localStorage.getItem('session_token');
    if (t) _setHeader(config, 'Authorization', `Bearer ${t}`);
    // 2) Si hay sesión de Facturador (rol restringido), agregamos su header
    //    propio (los endpoints `/admin/erp/users/facturador/...` lo leen).
    const ft = localStorage.getItem('facturador_token');
    if (ft) _setHeader(config, 'X-Facturador-Token', ft);
    // 3) Si hay sesión de Admin legacy (login TOTP tradicional), agregamos
    //    X-Admin-Token para que los endpoints /admin/* protegidos con
    //    _require_admin_session la reconozcan además del Bearer/cookie Google.
    const at = localStorage.getItem('bebida-bacana-admin-token');
    if (at) _setHeader(config, 'X-Admin-Token', at);
    // 4) Cache busting solo para /auth/me (verificación de sesión — siempre
    //    debe ir fresh, ignoramos caches intermedios de Cloudflare/browser).
    if (config.url && config.url.includes('/auth/me')) {
      _setHeader(config, 'Cache-Control', 'no-cache, no-store, must-revalidate');
      _setHeader(config, 'Pragma', 'no-cache');
    }
  } catch {}
  return config;
});

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🟡 True cuando el fetch de /store-settings falló repetidamente (>3
  // reintentos). Se muestra un banner discreto "Sincronizando últimos
  // precios..." en el header hasta que el fetch recupere.
  const [settingsSyncing, setSettingsSyncing] = useState(false);
  // 🟢 True cuando storeSettings tiene datos REALES (ya sea por fetch exitoso
  // o por cache válido de localStorage). Mientras sea false, componentes como
  // ProductCard NO deben asumir "0% descuento" — hay una ventana real donde
  // cartDiscountTiers=[] simplemente porque /store-settings todavía no
  // resolvió (detectado en iter170: usuarios con conexión lenta veían la
  // tarjeta "sin descuento" hasta que la data llegaba, pareciendo un bug).
  const [settingsReady, setSettingsReady] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('bebida-bacana-store-settings') : null;
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && typeof cached === 'object') return true;
      }
    } catch { /* JSON malformado */ }
    return false;
  });
  const [storeSettings, setStoreSettings] = useState(() => {
    // 🚀 Hidratación instantánea desde localStorage para evitar el "flicker"
    // de la barra de descuentos (que espera cartDiscountTiers y retorna null
    // mientras el fetch está en curso). Los defaults quedan como fallback si
    // no hay cache previo o si el JSON está corrupto.
    const defaults = {
      retailSurchargePercent: 5,
      retailMinimumPurchase: 50000,
      cartDiscountTiers: [],
      cartDiscountAudience: 'all',
    };
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('bebida-bacana-store-settings') : null;
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && typeof cached === 'object') return { ...defaults, ...cached };
      }
    } catch { /* JSON malformado, usar defaults */ }
    return defaults;
  });

  // Function to clear session-related local data when session is invalid
  const clearAllLocalData = useCallback(() => {
    console.log('[Auth] Clearing session data...');
    // Only remove Google session keys, PRESERVE admin login
    localStorage.removeItem('session_token');
    sessionStorage.clear();
    console.log('[Auth] Session data cleared');
  }, []);

  // Full logout: clear everything
  const clearEverything = useCallback(() => {
    console.log('[Auth] Full logout - clearing ALL data...');
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('bebida-bacana') || key === 'session_token')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // No borramos el flag de admin_login (bebida-bacana-admin) porque
    // ese loop ya lo cubre. Sí borramos el intent y guest-type para
    // que al volver a la app, el WelcomeGate re-aparezca limpio.
    sessionStorage.clear();
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Add cache-busting timestamp to force fresh data (headers no-cache
      // se agregan en el interceptor para no reemplazar el objeto headers).
      const response = await axios.get(`${API}/auth/me?_t=${Date.now()}`);
      setUser(response.data);
    } catch (error) {
      // CRITICAL: If 401, the session is invalid - clear ALL local data
      if (error.response?.status === 401) {
        clearAllLocalData();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [clearAllLocalData]);

  // Refresh user data from server - called automatically when user returns to app
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me?_t=${Date.now()}`);
      // Only update if data changed
      setUser(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(response.data)) {
          return response.data;
        }
        return prev;
      });
      return response.data;
    } catch (error) {
      // If user session is invalid, clear everything
      if (error.response?.status === 401) {
        clearAllLocalData();
        setUser(null);
      }
      return null;
    }
  }, [clearAllLocalData]);

  // AUTO-REFRESH: When user returns to the app, refresh their data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        refreshUser();
      }
    };

    const handleFocus = () => {
      if (user) {
        refreshUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, refreshUser]);

  const fetchStoreSettings = async (attempt = 0) => {
    // 🛡️ Resiliencia: si /store-settings falla (503 típico bajo carga alta,
    // 502 detrás del proxy, timeout), NO reseteamos el state — mantenemos
    // los últimos datos válidos (state actual o cache de localStorage) y
    // reintentamos con backoff exponencial. Los primeros 3 intentos son
    // silenciosos; a partir del 4to mostramos "Sincronizando últimos
    // precios..." en el header (state `settingsSyncing`).
    try {
      const { cachedGet } = await import('../utils/cachedGet');
      const data = await cachedGet(`${API}/store-settings`, { ttl: 60_000 });
      setStoreSettings(data);
      setSettingsReady(true);
      setSettingsSyncing(false);
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('bebida-bacana-store-settings', JSON.stringify(data));
        }
      } catch { /* storage lleno o modo privado */ }
    } catch (error) {
      // No borramos el state actual. Reintentamos con backoff acotado.
      const isServerErr = !error?.response || (error.response.status >= 500 && error.response.status < 600);
      if (attempt >= 3) setSettingsSyncing(true);
      if (isServerErr && attempt < 6) {
        // Backoff: 2s, 5s, 15s, 45s, 90s, 180s (max 6 reintentos ≈ 5.5min).
        const delays = [2000, 5000, 15000, 45000, 90000, 180000];
        const delay = delays[Math.min(attempt, delays.length - 1)];
        setTimeout(() => { fetchStoreSettings(attempt + 1); }, delay);
      } else {
        // Error no recuperable (400/401/403) o max attempts alcanzado.
        console.error('Error fetching store settings:', error);
      }
    }
  };

  useEffect(() => {
    // Google OAuth nativo: al volver de /api/auth/google/callback el
    // backend nos redirige a `/#session_token=...`. Capturamos el hash
    // ANTES del primer checkAuth para persistir el token en localStorage
    // (Bearer fallback) y limpiar la URL. La cookie httponly ya se
    // seteó del lado del backend, pero en Safari/iOS con ITP la cookie
    // cross-site puede desaparecer, por eso reforzamos con Bearer.
    try {
      const hash = window.location.hash || '';
      const m = hash.match(/session_token=([^&]+)/);
      if (m && m[1]) {
        localStorage.setItem('session_token', m[1]);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch { /* SSR or storage locked */ }
    checkAuth();
    fetchStoreSettings();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Google OAuth NATIVO (self-hosted). El backend se encarga del flow
  // completo: /api/auth/google/start genera state + redirige a Google,
  // y /api/auth/google/callback vuelve con el user creado y setea la
  // cookie de sesión. Sin branding intermedio.
  const loginWithGoogle = () => {
    window.location.href = `${API}/auth/google/start`;
  };

  // Login con email + contraseña (alternativa a Google).
  const loginWithEmail = async (email, password) => {
    const r = await axios.post(`${API}/auth/login-email`, { email, password });
    const token = r.data?.session_token;
    if (token) {
      try { localStorage.setItem('session_token', token); } catch {}
    }
    const { session_token: _omit, ...userData } = r.data || {};
    setUser(userData);
    return userData;
  };

  // Registro con email + contraseña.
  const registerWithEmail = async ({ email, password, name, customer_type, whatsapp }) => {
    const r = await axios.post(`${API}/auth/register-email`, { email, password, name, customer_type, whatsapp });
    const token = r.data?.session_token;
    if (token) {
      try { localStorage.setItem('session_token', token); } catch {}
    }
    const { session_token: _omit, ...userData } = r.data || {};
    setUser(userData);
    return userData;
  };

  const exchangeSession = async (sessionId) => {
    try {
      // We deliberately do NOT pass withCredentials here. In cross-origin deploys
      // the proxy returns ACAO: * which makes credentials-mode preflight fail.
      // Same-origin cookies are still set/received by the browser regardless.
      const response = await axios.post(`${API}/auth/session`,
        { session_id: sessionId }
      );
      // Persist Bearer fallback for cross-origin deploys where cookie is blocked
      const token = response.data?.session_token;
      if (token) {
        try { localStorage.setItem('session_token', token); } catch {}
      }
      // Strip token from user state (don't expose it in components)
      const { session_token: _omit, ...userData } = response.data || {};
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error exchanging session:', error);
      throw error;
    }
  };

  const updateCustomerType = async (customerType) => {
    try {
      const response = await axios.put(`${API}/auth/customer-type`,
        { customer_type: customerType },
        { withCredentials: true }
      );
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating customer type:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      clearEverything();
      setUser(null);
    }
  };

  // Calculate price with surcharge for retail customers
  // Surcharge only applies if cart total is below wholesaleThreshold
  const _adminPreviewType = () => {
    // Solo se aplica si el usuario es admin Y eligió un modo de vista previa
    const isAdmin = user?.role === 'admin' || (typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true');
    if (!isAdmin) return null;
    const v = typeof window !== 'undefined' ? localStorage.getItem('bebida-bacana-admin-preview-type') : null;
    return (v && v !== 'real') ? v : null;
  };

  // ─────────────────────────────────────────────────────────────
  // Helper para obtener el "guest type" elegido en la WelcomeGate.
  // Se usa cuando NO hay user autenticado para igualmente conocer si el
  // visitante eligió "Soy Particular" o "Soy Comercio".
  //
  // SECURITY: el modo "guest comercio" fue eliminado. Si por algún
  // motivo (storage stale de versión previa) aparece 'comercio' sin
  // user real, lo ignoramos y tratamos como 'consumidor_final' para
  // evitar mostrar precios mayoristas a quien no inició sesión.
  // ─────────────────────────────────────────────────────────────
  const _guestType = () => {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem('bebida-bacana-guest-type');
    if (raw === 'comercio' && !user) return null; // ignorar comercio guest sin login
    return raw;
  };

  const _effectiveCustomerTypeRaw = () => {
    const adminPreview = _adminPreviewType();
    if (adminPreview) return adminPreview;
    if (user?.role === 'seller') {
      return localStorage.getItem('bebida-bacana-seller-client-type') || user?.customer_type;
    }
    // Admin sin preview activo: no asumimos retail (no aplicar mínimos a su navegación interna).
    if (user?.role === 'admin') return user?.customer_type || null;
    // Cliente normal: si no hay customer_type ni guest_type explícito,
    // tratamos como consumidor_final (regla: mínimo y reglas retail por defecto).
    return user?.customer_type || _guestType() || 'consumidor_final';
  };

  const getAdjustedPrice = (basePrice, cartTotal = 0) => {
    const effectiveType = _effectiveCustomerTypeRaw();
    let price = basePrice;
    // 1) Recargo Consumidor Final (si aplica y no llegó al threshold mayorista).
    if (effectiveType === 'consumidor_final') {
      const threshold = storeSettings.retailWholesaleThreshold || 100000;
      if (cartTotal < threshold) {
        const surcharge = storeSettings.retailSurchargePercent || 0;
        price = price * (1 + surcharge / 100);
      }
    }
    // 2) Descuento permanente del cliente logueado (ambos tipos de user).
    const permDisc = Number(user?.permanentDiscountPct || 0);
    if (permDisc > 0) {
      price = price * (1 - permDisc / 100);
    }
    return Math.ceil(price);
  };

  // ─────────────────────────────────────────────────────────────
  // Descuento por objetivo de carrito (multi-tier, winner-takes-all
  // vs permanentDiscountPct y vs tier por cantidad).
  // ─────────────────────────────────────────────────────────────
  const _cartDiscountEligibleForUser = () => {
    // 🎯 Respeta SIEMPRE la audiencia configurada según el tipo de cliente
    // EFECTIVO (incluye preview de admin y cliente seleccionado por vendedor).
    // Antes había un bypass que mostraba el descuento a admin/vendedor aunque
    // el cliente fuera consumidor final → ese era el bug reportado.
    const audience = storeSettings.cartDiscountAudience || 'all';
    if (audience === 'all') return true;
    const eff = _effectiveCustomerTypeRaw();
    if (audience === 'b2c') return eff === 'consumidor_final';
    if (audience === 'b2b') return eff === 'comercio';
    return true;
  };

  // Devuelve el mayor % de descuento por objetivo alcanzado dado un cartTotal.
  // Si el user no es elegible, devuelve 0.
  const getBestCartDiscountPct = (cartTotal) => {
    // Cliente con descuento fijo (permanente): el objetivo NO aplica (es único).
    if (Number(user?.permanentDiscountPct || 0) > 0) return 0;
    if (!_cartDiscountEligibleForUser()) return 0;
    const tiers = Array.isArray(storeSettings.cartDiscountTiers) ? storeSettings.cartDiscountTiers : [];
    if (tiers.length === 0) return 0;
    let bestPct = 0;
    for (const t of tiers) {
      const threshold = Number(t?.threshold || 0);
      const pct = Number(t?.pct || 0);
      if (threshold > 0 && cartTotal >= threshold && pct > bestPct) bestPct = pct;
    }
    return bestPct;
  };

  // Devuelve el próximo tier a alcanzar (para la barra de progreso).
  // { threshold, pct, missing } | null si ya alcanzó el máximo o no aplica.
  const getNextCartDiscountTier = (cartTotal) => {
    if (Number(user?.permanentDiscountPct || 0) > 0) return null;
    if (!_cartDiscountEligibleForUser()) return null;
    const tiers = Array.isArray(storeSettings.cartDiscountTiers) ? storeSettings.cartDiscountTiers : [];
    if (tiers.length === 0) return null;
    const sorted = [...tiers]
      .filter(t => Number(t?.threshold || 0) > 0 && Number(t?.pct || 0) > 0)
      .sort((a, b) => Number(a.threshold) - Number(b.threshold));
    for (const t of sorted) {
      if (cartTotal < Number(t.threshold)) {
        return { threshold: Number(t.threshold), pct: Number(t.pct), missing: Number(t.threshold) - cartTotal };
      }
    }
    return null;
  };

  // Winner-takes-all: elige el MAYOR % entre permanente del cliente, cart
  // discount, y tier por cantidad. TODO calculado sobre priceNumeric (base).
  // - Si el producto está excluido del cart discount, se ignora ese %.
  // - Consumidor final NO recibe tiers por cantidad (regla existente).
  // - Si el método de pago seleccionado tiene `allowVolumeDiscounts=false`,
  //   se ignoran TODOS los descuentos por volumen (tier por cantidad + cart
  //   discount). El permanente del cliente NUNCA se ignora (es de perfil).
  const getBestDiscountPct = (product, cartTotal, quantity, paymentMethod = null) => {
    const permDisc = Number(user?.permanentDiscountPct || 0);
    // 🚦 Feature flag: si productQuantityDiscounts está OFF, ignoramos los
    // tiers por cantidad en TODOS los cálculos.
    const _ff = storeSettings?.featureFlags || {};
    const ffQtyDisc = typeof _ff.productQuantityDiscounts === 'boolean' ? _ff.productQuantityDiscounts : true;
    const pmBlocksVolume = paymentMethod && paymentMethod.allowVolumeDiscounts === false;
    const cartDisc = (pmBlocksVolume || product?.excludedFromCartDiscount) ? 0 : getBestCartDiscountPct(cartTotal);
    let tierDisc = 0;
    const eff = _effectiveCustomerTypeRaw();
    const canGetQtyTier = ffQtyDisc && eff !== 'consumidor_final' && !pmBlocksVolume;
    if (canGetQtyTier && Array.isArray(product?.quantityPrices) && product.quantityPrices.length > 0) {
      const base = Number(product.priceNumeric || 0);
      const sorted = [...product.quantityPrices].sort((a, b) => (b.minQty || 0) - (a.minQty || 0));
      for (const t of sorted) {
        if (quantity >= (t.minQty || 0)) {
          const tp = Number(t.priceNumeric || 0);
          if (base > 0 && tp > 0 && tp < base) {
            tierDisc = ((base - tp) / base) * 100;
          }
          break;
        }
      }
    }
    return Math.max(permDisc, cartDisc, tierDisc);
  };

  // A3 · Descuento fijo (permanente) del cliente = ÚNICO descuento.
  // Si el cliente tiene un descuento permanente configurado (ej. Carlos 6%),
  // ese % REEMPLAZA a cualquier otro descuento (objetivo/cantidad). No se apila.
  const getWinnerDiscountPct = (product, cartTotal, quantity, paymentMethod = null) => {
    const permDisc = Number(user?.permanentDiscountPct || 0);
    if (permDisc > 0) return permDisc;
    return getBestDiscountPct(product, cartTotal, quantity, paymentMethod);
  };

  // Precio unitario FINAL considerando winner-takes-all + recargo B2C.
  const getFinalUnitPrice = (product, cartTotal = 0, quantity = 1, paymentMethod = null) => {
    let price = Number(product?.priceNumeric || 0);
    // Winner-takes-all sobre precio BASE. getWinnerDiscountPct devuelve el
    // descuento permanente del cliente como ÚNICO si existe (reemplaza a los demás).
    const winnerPct = getWinnerDiscountPct(product, cartTotal, quantity, paymentMethod);
    if (winnerPct > 0) {
      price = price * (1 - winnerPct / 100);
    }
    // Recargo B2C aparte (es categoría de cliente, no un "descuento").
    const eff = _effectiveCustomerTypeRaw();
    if (eff === 'consumidor_final') {
      const threshold = storeSettings.retailWholesaleThreshold || 100000;
      if (cartTotal < threshold) {
        const surcharge = storeSettings.retailSurchargePercent || 0;
        price = price * (1 + surcharge / 100);
      }
    }
    return Math.ceil(price);
  };

  // Precio "listado" — con recargo aplicado si consumidor final, pero SIN
  // el descuento permanente. Se usa para mostrar el precio tachado cuando
  // el cliente tiene descuento permanente > 0.
  const getListedPrice = (basePrice, cartTotal = 0) => {
    const effectiveType = _effectiveCustomerTypeRaw();
    let price = basePrice;
    if (effectiveType === 'consumidor_final') {
      const threshold = storeSettings.retailWholesaleThreshold || 100000;
      if (cartTotal < threshold) {
        const surcharge = storeSettings.retailSurchargePercent || 0;
        price = price * (1 + surcharge / 100);
      }
    }
    return Math.ceil(price);
  };

  // Check if retail customer gets wholesale prices (above threshold)
  const getsWholesalePrices = (cartTotal) => {
    const effectiveType = _effectiveCustomerTypeRaw();
    if (effectiveType !== 'consumidor_final') return true;
    const threshold = storeSettings.retailWholesaleThreshold || 100000;
    return cartTotal >= threshold;
  };

  // Check if minimum purchase is met for retail customers (and guests).
  const meetsMinimumPurchase = (cartTotal) => {
    const effectiveType = _effectiveCustomerTypeRaw();
    if (effectiveType !== 'consumidor_final') return true; // sin mínimo para mayoristas
    return cartTotal >= (storeSettings.retailMinimumPurchase || 0);
  };

  // Get effective customer type (considers admin preview + seller override + guest mode)
  const getEffectiveCustomerType = () => _effectiveCustomerTypeRaw() || null;

  // ─────────────────────────────────────────────────────────────
  // Sistema de Premios por Volumen (gift tiers)
  // ─────────────────────────────────────────────────────────────
  // Elegibilidad por audiencia POR TIER (independiente del descuento %).
  const _giftEligibleForTier = (tier) => {
    const audience = (tier && tier.giftAudience) || 'all';
    if (audience === 'all') return true;
    const eff = _effectiveCustomerTypeRaw();
    if (audience === 'b2c') return eff === 'consumidor_final';
    if (audience === 'b2b') return eff === 'comercio';
    return true;
  };

  // ¿El tier tiene un regalo entregable? Requiere:
  //  - giftProductId configurado
  //  - producto existente (snapshot presente)
  //  - stock suficiente (o stock indefinido = sin control)
  //  - audiencia del regalo compatible con el cliente
  const _tierHasDeliverableGift = (tier) => {
    if (!tier || !tier.giftProductId) return false;
    const p = tier.giftProduct;
    if (!p || p.active === false) return false;
    if (!_giftEligibleForTier(tier)) return false;
    const needed = Number(tier.giftQty || 1);
    if (p.stock !== undefined && p.stock !== null && Number(p.stock) < needed) return false;
    return true;
  };

  // Devuelve el regalo del tier MÁS ALTO alcanzado dado un cartTotal.
  // Si el tier alcanzado no tiene regalo entregable (sin stock, sin config,
  // audiencia no elegible), busca el siguiente tier alcanzado hacia abajo.
  // Retorna null si no hay ningún regalo entregable.
  const getReachedGiftTier = (cartTotal) => {
    const tiers = Array.isArray(storeSettings.cartDiscountTiers) ? storeSettings.cartDiscountTiers : [];
    if (tiers.length === 0) return null;
    const reached = tiers
      .filter(t => Number(t?.threshold || 0) > 0 && cartTotal >= Number(t.threshold))
      .sort((a, b) => Number(b.threshold) - Number(a.threshold));
    for (const t of reached) {
      if (_tierHasDeliverableGift(t)) return t;
    }
    return null;
  };

  // Devuelve el próximo tier CON regalo entregable a alcanzar (para el
  // preview de la barra de progreso). Salta tiers sin regalo o sin stock.
  const getNextGiftTier = (cartTotal) => {
    const tiers = Array.isArray(storeSettings.cartDiscountTiers) ? storeSettings.cartDiscountTiers : [];
    if (tiers.length === 0) return null;
    const sorted = [...tiers]
      .filter(t => Number(t?.threshold || 0) > 0 && _tierHasDeliverableGift(t))
      .sort((a, b) => Number(a.threshold) - Number(b.threshold));
    for (const t of sorted) {
      if (cartTotal < Number(t.threshold)) {
        return { ...t, missing: Number(t.threshold) - cartTotal };
      }
    }
    return null;
  };


  const value = {
    user,
    loading,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    exchangeSession,
    updateCustomerType,
    logout,
    checkAuth,
    refreshUser,
    storeSettings,
    settingsSyncing,
    settingsReady,
    featureFlags: (storeSettings && storeSettings.featureFlags) || {},
    isFeatureEnabled: (key) => {
      // Helper — retorna true si la feature está ON. Defaults conservadores:
      // b2cFlow / paymentMethodDiscounts / productQuantityDiscounts /
      // giftSystem / customBanners / priceListDownload / smartSearch /
      // erpModule → true; helperAccounts → false (feature nueva sin
      // implementación completa).
      const flags = (storeSettings && storeSettings.featureFlags) || {};
      const defaults = {
        b2cFlow: true, paymentMethodDiscounts: true, productQuantityDiscounts: true,
        giftSystem: true, customBanners: true, helperAccounts: false,
        priceListDownload: true, smartSearch: true, erpModule: true,
      };
      const v = flags[key];
      return typeof v === 'boolean' ? v : (defaults[key] !== undefined ? defaults[key] : true);
    },
    getAdjustedPrice,
    getListedPrice,
    getsWholesalePrices,
    meetsMinimumPurchase,
    getEffectiveCustomerType,
    getBestCartDiscountPct,
    getNextCartDiscountTier,
    getReachedGiftTier,
    getNextGiftTier,
    getBestDiscountPct,
    getWinnerDiscountPct,
    getFinalUnitPrice,
    isRetail: _effectiveCustomerTypeRaw() === 'consumidor_final',
    isWholesale: _effectiveCustomerTypeRaw() === 'comercio',
    needsCustomerType: user && !user.customer_type
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
